import SEOHead from '../components/SEOHead'
import ContactForm from '../components/ContactForm'
import { useCmsPage } from '../hooks/useCmsPage'

export default function KontaktPage() {
  const { page } = useCmsPage('kontakt')

  const title = page?.meta_title || 'KONTAKT — Casper DJ'
  const description =
    page?.meta_description ||
    'Kontakt Casper DJ på +45 30 40 50 06 eller INFO@CASPERDJ.DK for en uforpligtende samtale om musik og DJ til din næste fest.'

  return (
    <div className="bg-neutral-950 text-white min-h-screen">
      <SEOHead title={title} description={description} />

      {/* Header */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 border-b border-neutral-900 bg-neutral-900/20 text-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
          <span className="text-xs font-semibold tracking-widest text-gold-400 uppercase">
            Kom i kontakt
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
            KONTAKT
          </h1>
          <p className="text-neutral-300 text-sm sm:text-base max-w-xl mx-auto">
            Kontakt CasperDJ for en uforpligtende snak om dit næste arrangement. Jeg står klar til at hjælpe dig med den perfekte musikalske oplevelse.
          </p>
        </div>
      </section>

      {/* Content grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* 3 boxes in one row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
            <a
              href="tel:+4530405006"
              className="p-6 rounded-2xl bg-neutral-900/60 border border-neutral-800 hover:border-gold-400/50 transition-colors flex items-center gap-4 group"
            >
              <div className="w-12 h-12 rounded-xl bg-gold-400 text-black flex items-center justify-center shrink-0 text-lg">
                <i className="fa-solid fa-phone" aria-hidden="true" />
              </div>
              <div>
                <span className="block text-xs text-neutral-400 font-semibold uppercase tracking-wider">
                  Ring direkte
                </span>
                <span className="text-lg font-bold text-white group-hover:text-gold-400 transition-colors whitespace-nowrap">
                  +45 30 40 50 06
                </span>
              </div>
            </a>

            <a
              href="mailto:info@casperdj.dk"
              className="p-6 rounded-2xl bg-neutral-900/60 border border-neutral-800 hover:border-gold-400/50 transition-colors flex items-center gap-4 group"
            >
              <div className="w-12 h-12 rounded-xl bg-gold-400 text-black flex items-center justify-center shrink-0 text-lg">
                <i className="fa-solid fa-envelope" aria-hidden="true" />
              </div>
              <div>
                <span className="block text-xs text-neutral-400 font-semibold uppercase tracking-wider">
                  Skriv en email
                </span>
                <span className="text-lg font-bold text-white group-hover:text-gold-400 transition-colors whitespace-nowrap lowercase">
                  info@casperdj.dk
                </span>
              </div>
            </a>

            <div className="p-6 rounded-2xl bg-neutral-900/40 border border-neutral-800 flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-neutral-800 text-gold-400 flex items-center justify-center shrink-0 text-lg">
                <i className="fa-solid fa-id-card" aria-hidden="true" />
              </div>
              <div>
                <span className="block text-xs text-neutral-400 font-semibold uppercase tracking-wider">
                  CVR-nummer
                </span>
                <span className="text-sm text-neutral-300 font-medium">
                  cvr: 37688967<br />
                  <span className="text-xs text-neutral-400">Kører ud i hele Danmark efter aftale</span>
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-stretch">
            {/* Contact details & portrait */}
            <div className="lg:col-span-5 flex flex-col">
              {/* Image card */}
              <div className="relative flex-1 min-h-[420px] rounded-3xl overflow-hidden border border-neutral-800 shadow-2xl bg-neutral-900">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/casperdj0184-ricofeldfoss-1.jpg"
                  alt="Casper DJ"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="text-xl font-bold text-white">Casper DJ</h3>
                  <p className="text-xs text-gold-400 font-medium mt-0.5">Professionel DJ til alle arrangementer</p>
                </div>
              </div>

              {/* Social links */}
              <div className="mt-6 p-4 rounded-2xl bg-neutral-900/60 border border-neutral-800 flex items-center justify-between gap-3">
                <span className="text-xs font-semibold text-neutral-400 uppercase tracking-wider">
                  Følg med
                </span>
                <div className="flex items-center gap-3">
                  <a
                    href="https://www.instagram.com/casperdj.dk/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                    className="w-10 h-10 rounded-xl bg-neutral-800/80 hover:bg-gold-400 hover:text-black text-neutral-300 transition-all flex items-center justify-center text-base"
                  >
                    <i className="fa-brands fa-instagram" aria-hidden="true" />
                  </a>
                  <a
                    href="https://www.facebook.com/casperdj.dk/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                    className="w-10 h-10 rounded-xl bg-neutral-800/80 hover:bg-gold-400 hover:text-black text-neutral-300 transition-all flex items-center justify-center text-base"
                  >
                    <i className="fa-brands fa-facebook-f" aria-hidden="true" />
                  </a>
                  <a
                    href="https://www.youtube.com/channel/UCcSTX6_ffrWVlw5hPM5L1EQ"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="YouTube"
                    className="w-10 h-10 rounded-xl bg-neutral-800/80 hover:bg-gold-400 hover:text-black text-neutral-300 transition-all flex items-center justify-center text-base"
                  >
                    <i className="fa-brands fa-youtube" aria-hidden="true" />
                  </a>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-7 flex flex-col">
              <ContactForm
                title="Send en besked"
                subtitle="Udfyld formularen, så vender jeg tilbage inden for kort tid."
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
